import { z, ZodSchema } from 'zod';
import pako from 'pako';

// Type definitions
export interface DataVersion {
  major: number;
  minor: number;
  patch: number;
}

export interface MigrationSchema {
  version: DataVersion;
  description: string;
  up: (data: unknown) => unknown | Promise<unknown>;
  down: (data: unknown) => unknown | Promise<unknown>;
  schema?: ZodSchema;
}

export interface BackupManifest {
  id: string;
  dataType: string;
  version: DataVersion;
  timestamp: Date;
  size: number;
  recordCount: number;
  compressed: boolean;
  metadata?: Record<string, unknown>;
}

export interface MigrationHistory {
  id: string;
  dataType: string;
  fromVersion: DataVersion;
  toVersion: DataVersion;
  timestamp: Date;
  success: boolean;
  recordsProcessed: number;
  duration: number;
  backupId?: string;
  error?: string;
}

export interface DataSnapshot {
  data: unknown;
  metadata: {
    id: string;
    dataType: string;
    version: DataVersion;
    timestamp: Date;
    checksum?: string;
    [key: string]: unknown;
  };
}

export interface ValidationOptions {
  checkRequired?: boolean;
  checkTypes?: boolean;
  checkReferences?: boolean;
  customValidators?: Array<(data: unknown) => boolean | Promise<boolean>>;
}

// Version comparison utilities
export function compareVersions(v1: DataVersion, v2: DataVersion): number {
  if (v1.major !== v2.major) {
    return v1.major - v2.major;
  }
  if (v1.minor !== v2.minor) {
    return v1.minor - v2.minor;
  }
  return v1.patch - v2.patch;
}

export function isVersionGreater(v1: DataVersion, v2: DataVersion): boolean {
  return compareVersions(v1, v2) > 0;
}

export function isVersionLess(v1: DataVersion, v2: DataVersion): boolean {
  return compareVersions(v1, v2) < 0;
}

export function isVersionEqual(v1: DataVersion, v2: DataVersion): boolean {
  return compareVersions(v1, v2) === 0;
}

export function parseVersion(versionString: string): DataVersion {
  const parts = versionString.split('.').map(p => parseInt(p, 10));
  
  if (parts.length !== 3 || parts.some(isNaN)) {
    throw new Error(`Invalid version string: ${versionString}`);
  }
  
  return {
    major: parts[0],
    minor: parts[1],
    patch: parts[2],
  };
}

export function formatVersion(version: DataVersion): string {
  return `${version.major}.${version.minor}.${version.patch}`;
}

// Data compression utilities
export async function compressData(data: unknown): Promise<string> {
  try {
    const jsonString = JSON.stringify(data);
    const compressed = pako.deflate(jsonString);
    return btoa(String.fromCharCode.apply(null, Array.from(compressed)));
  } catch (error) {
    throw new Error(`Failed to compress data: ${error.message}`);
  }
}

export async function decompressData(compressedData: string): Promise<unknown> {
  try {
    const binaryString = atob(compressedData);
    const bytes = new Uint8Array(binaryString.length);
    
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    const decompressed = pako.inflate(bytes, { to: 'string' });
    return JSON.parse(decompressed);
  } catch (error) {
    throw new Error(`Failed to decompress data: ${error.message}`);
  }
}

// Data snapshot utilities
export function createDataSnapshot(
  data: unknown,
  metadata: DataSnapshot['metadata']
): DataSnapshot {
  const snapshot: DataSnapshot = {
    data: structuredClone(data),
    metadata: {
      ...metadata,
      checksum: generateChecksum(data),
    },
  };
  
  return snapshot;
}

export function generateChecksum(data: unknown): string {
  const jsonString = JSON.stringify(data);
  let hash = 0;
  
  for (let i = 0; i < jsonString.length; i++) {
    const char = jsonString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
}

// Data validation utilities
export async function validateDataIntegrity(
  data: unknown,
  options: ValidationOptions = {}
): Promise<boolean> {
  try {
    // Check for required fields
    if (options.checkRequired) {
      if (data === null || data === undefined) {
        return false;
      }
    }

    // Check data types
    if (options.checkTypes) {
      if (!isValidDataStructure(data)) {
        return false;
      }
    }

    // Check references
    if (options.checkReferences) {
      if (!areReferencesValid(data)) {
        return false;
      }
    }

    // Run custom validators
    if (options.customValidators) {
      for (const validator of options.customValidators) {
        const isValid = await validator(data);
        if (!isValid) {
          return false;
        }
      }
    }

    return true;
  } catch (error) {
    console.error('Data validation error:', error);
    return false;
  }
}

function isValidDataStructure(data: unknown): boolean {
  if (data === null || data === undefined) {
    return true;
  }

  if (Array.isArray(data)) {
    return data.every(item => isValidDataStructure(item));
  }

  if (typeof data === 'object') {
    return Object.values(data).every(value => isValidDataStructure(value));
  }

  // Check for valid primitive types
  const validTypes = ['string', 'number', 'boolean'];
  return validTypes.includes(typeof data);
}

function areReferencesValid(data: unknown): boolean {
  // This is a simplified reference check
  // In a real implementation, you'd check foreign keys, etc.
  if (Array.isArray(data)) {
    return data.every(item => {
      if (typeof item === 'object' && item !== null && 'id' in item) {
        const id = (item as Record<string, unknown>).id;
        return typeof id === 'string' && id.length > 0;
      }
      return true;
    });
  }

  if (typeof data === 'object' && data !== null) {
    return Object.values(data).every(value => areReferencesValid(value));
  }

  return true;
}

// Data transformation utilities
export function transformDataStructure(
  data: unknown,
  transformations: Array<{
    path: string;
    transform: (value: unknown) => unknown;
  }>
): unknown {
  const result = structuredClone(data);
  
  for (const { path, transform } of transformations) {
    if (Array.isArray(result) || (typeof result === 'object' && result !== null)) {
      applyTransformationAtPath(result as Record<string, unknown> | unknown[], path.split('.'), transform);
    }
  }
  
  return result;
}

function applyTransformationAtPath(
  data: Record<string, unknown> | unknown[],
  pathParts: string[],
  transform: (value: unknown) => unknown
): void {
  if (pathParts.length === 0) {
    return;
  }

  const [current, ...rest] = pathParts;
  
  if (rest.length === 0) {
    // Apply transformation
    if (current === '*' && Array.isArray(data)) {
      // Apply to all array elements
      for (let i = 0; i < data.length; i++) {
        data[i] = transform(data[i]);
      }
    } else if (current === '*' && typeof data === 'object' && data !== null) {
      // Apply to all object values
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          data[key] = transform(data[key]);
        }
      }
    } else if (data && typeof data === 'object' && current in data) {
      data[current] = transform(data[current]);
    }
  } else {
    // Recurse deeper
    if (current === '*' && Array.isArray(data)) {
      for (const item of data) {
        applyTransformationAtPath(item as Record<string, unknown> | unknown[], rest, transform);
      }
    } else if (current === '*' && typeof data === 'object' && data !== null) {
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          applyTransformationAtPath(data[key] as Record<string, unknown> | unknown[], rest, transform);
        }
      }
    } else if (data && typeof data === 'object' && current in data) {
      applyTransformationAtPath(data[current] as Record<string, unknown> | unknown[], rest, transform);
    }
  }
}

// Schema evolution utilities
export function evolveSchema(
  oldSchema: ZodSchema,
  changes: {
    addFields?: Record<string, ZodSchema>;
    removeFields?: string[];
    modifyFields?: Record<string, (field: ZodSchema) => ZodSchema>;
  }
): ZodSchema {
  // This is a simplified implementation
  // In a real scenario, you'd need to handle nested schemas properly
  
  if (oldSchema instanceof z.ZodObject) {
    let shape = { ...oldSchema.shape };
    
    // Add new fields
    if (changes.addFields) {
      shape = { ...shape, ...changes.addFields };
    }
    
    // Remove fields
    if (changes.removeFields) {
      for (const field of changes.removeFields) {
        delete shape[field];
      }
    }
    
    // Modify fields
    if (changes.modifyFields) {
      for (const [field, modifier] of Object.entries(changes.modifyFields)) {
        if (shape[field]) {
          shape[field] = modifier(shape[field]);
        }
      }
    }
    
    return z.object(shape);
  }
  
  return oldSchema;
}

// Migration helper utilities
export function createSimpleMigration(
  version: DataVersion,
  description: string,
  transforms: {
    up: Array<{ path: string; transform: (value: unknown) => unknown }>;
    down: Array<{ path: string; transform: (value: unknown) => unknown }>;
  },
  schema?: ZodSchema
): MigrationSchema {
  return {
    version,
    description,
    up: (data: unknown) => transformDataStructure(data, transforms.up),
    down: (data: unknown) => transformDataStructure(data, transforms.down),
    schema,
  };
}

export function createFieldRenameMigration(
  version: DataVersion,
  description: string,
  renames: Array<{ from: string; to: string }>,
  schema?: ZodSchema
): MigrationSchema {
  return {
    version,
    description,
    up: (data: unknown) => {
      const result = structuredClone(data);
      
      if (Array.isArray(result)) {
        result.forEach(item => applyRenames(item as Record<string, unknown>, renames, false));
      } else if (typeof result === 'object' && result !== null) {
        applyRenames(result as Record<string, unknown>, renames, false);
      }
      
      return result;
    },
    down: (data: unknown) => {
      const result = structuredClone(data);
      
      if (Array.isArray(result)) {
        result.forEach(item => applyRenames(item as Record<string, unknown>, renames, true));
      } else if (typeof result === 'object' && result !== null) {
        applyRenames(result as Record<string, unknown>, renames, true);
      }
      
      return result;
    },
    schema,
  };
}

function applyRenames(
  obj: Record<string, unknown>,
  renames: Array<{ from: string; to: string }>,
  reverse: boolean
): void {
  for (const { from, to } of renames) {
    const sourceKey = reverse ? to : from;
    const targetKey = reverse ? from : to;
    
    if (sourceKey in obj) {
      obj[targetKey] = obj[sourceKey];
      delete obj[sourceKey];
    }
  }
}

export function createDefaultValueMigration(
  version: DataVersion,
  description: string,
  defaults: Record<string, unknown>,
  schema?: ZodSchema
): MigrationSchema {
  return {
    version,
    description,
    up: (data: unknown) => {
      const result = structuredClone(data);
      
      if (Array.isArray(result)) {
        result.forEach(item => applyDefaults(item as Record<string, unknown>, defaults));
      } else if (typeof result === 'object' && result !== null) {
        applyDefaults(result as Record<string, unknown>, defaults);
      }
      
      return result;
    },
    down: (data: unknown) => {
      const result = structuredClone(data);
      
      if (Array.isArray(result)) {
        result.forEach(item => removeDefaults(item as Record<string, unknown>, defaults));
      } else if (typeof result === 'object' && result !== null) {
        removeDefaults(result as Record<string, unknown>, defaults);
      }
      
      return result;
    },
    schema,
  };
}

function applyDefaults(obj: Record<string, unknown>, defaults: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(defaults)) {
    if (!(key in obj)) {
      obj[key] = structuredClone(value);
    }
  }
}

function removeDefaults(obj: Record<string, unknown>, defaults: Record<string, unknown>): void {
  for (const key of Object.keys(defaults)) {
    if (key in obj && JSON.stringify(obj[key]) === JSON.stringify(defaults[key])) {
      delete obj[key];
    }
  }
}

// Batch migration utilities
export function createBatchMigration(
  migrations: MigrationSchema[]
): MigrationSchema {
  const sortedMigrations = migrations.sort((a, b) => compareVersions(a.version, b.version));
  const lastVersion = sortedMigrations[sortedMigrations.length - 1].version;
  
  return {
    version: lastVersion,
    description: `Batch migration to ${formatVersion(lastVersion)}`,
    up: async (data: unknown) => {
      let result = data;
      
      for (const migration of sortedMigrations) {
        result = await migration.up(result);
      }
      
      return result;
    },
    down: async (data: unknown) => {
      let result = data;
      
      for (const migration of [...sortedMigrations].reverse()) {
        result = await migration.down(result);
      }
      
      return result;
    },
  };
}

// Safe migration utilities
export function createSafeMigration(
  migration: MigrationSchema,
  options: {
    validateBefore?: ZodSchema;
    validateAfter?: ZodSchema;
    onError?: (error: Error) => unknown;
  } = {}
): MigrationSchema {
  return {
    ...migration,
    up: async (data: unknown) => {
      try {
        // Validate before migration
        if (options.validateBefore) {
          const validation = options.validateBefore.safeParse(data);
          if (!validation.success) {
            throw new Error(`Pre-migration validation failed: ${validation.error.message}`);
          }
        }

        // Run migration
        const result = await migration.up(data);

        // Validate after migration
        if (options.validateAfter) {
          const validation = options.validateAfter.safeParse(result);
          if (!validation.success) {
            throw new Error(`Post-migration validation failed: ${validation.error.message}`);
          }
        }

        return result;
      } catch (error) {
        if (options.onError) {
          return options.onError(error as Error);
        }
        throw error;
      }
    },
    down: async (data: unknown) => {
      try {
        return await migration.down(data);
      } catch (error) {
        if (options.onError) {
          return options.onError(error as Error);
        }
        throw error;
      }
    },
  };
}

// Conditional migration utilities
export function createConditionalMigration(
  version: DataVersion,
  description: string,
  condition: (data: unknown) => boolean | Promise<boolean>,
  ifTrue: MigrationSchema,
  ifFalse?: MigrationSchema
): MigrationSchema {
  return {
    version,
    description,
    up: async (data: unknown) => {
      const shouldApply = await condition(data);
      
      if (shouldApply) {
        return await ifTrue.up(data);
      } else if (ifFalse) {
        return await ifFalse.up(data);
      }
      
      return data;
    },
    down: async (data: unknown) => {
      const shouldApply = await condition(data);
      
      if (shouldApply) {
        return await ifTrue.down(data);
      } else if (ifFalse) {
        return await ifFalse.down(data);
      }
      
      return data;
    },
  };
}

// Data cleaning utilities
export function cleanupData(
  data: unknown,
  options: {
    removeNull?: boolean;
    removeUndefined?: boolean;
    removeEmpty?: boolean;
    trimStrings?: boolean;
  } = {}
): unknown {
  if (data === null && options.removeNull) {
    return undefined;
  }
  
  if (data === undefined && options.removeUndefined) {
    return undefined;
  }
  
  if (Array.isArray(data)) {
    const cleaned = data
      .map(item => cleanupData(item, options))
      .filter(item => item !== undefined);
    
    return options.removeEmpty && cleaned.length === 0 ? undefined : cleaned;
  }
  
  if (typeof data === 'object' && data !== null) {
    const cleaned: Record<string, unknown> = {};
    
    for (const [key, value] of Object.entries(data)) {
      const cleanedValue = cleanupData(value, options);
      
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue;
      }
    }
    
    return options.removeEmpty && Object.keys(cleaned).length === 0 ? undefined : cleaned;
  }
  
  if (typeof data === 'string' && options.trimStrings) {
    const trimmed = data.trim();
    return options.removeEmpty && trimmed === '' ? undefined : trimmed;
  }
  
  return data;
}

// Export utilities
export function exportDataForMigration(data: unknown, format: 'json' | 'yaml' = 'json'): string {
  if (format === 'json') {
    return JSON.stringify(data, null, 2);
  }
  
  // Simplified YAML-like format
  return stringifyToYaml(data, 0);
}

function stringifyToYaml(data: unknown, indent: number): string {
  const spaces = ' '.repeat(indent);
  
  if (data === null) {
    return `${spaces}null`;
  }
  
  if (data === undefined) {
    return `${spaces}undefined`;
  }
  
  if (typeof data === 'string') {
    return `${spaces}"${data}"`;
  }
  
  if (typeof data === 'number' || typeof data === 'boolean') {
    return `${spaces}${data}`;
  }
  
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return `${spaces}[]`;
    }
    
    return data.map((item) => 
      `${spaces}- ${stringifyToYaml(item, 0).trim()}`
    ).join('\n');
  }
  
  if (typeof data === 'object') {
    const entries = Object.entries(data);
    
    if (entries.length === 0) {
      return `${spaces}{}`;
    }
    
    return entries.map(([key, value]) => 
      `${spaces}${key}:\n${stringifyToYaml(value, indent + 2)}`
    ).join('\n');
  }
  
  return `${spaces}${String(data)}`;
}